import type { StoreApi, UseBoundStore } from 'zustand';
import {
  createDataSourceControllerSliceKeysSelectors,
  createDataSourceControllerSliceSelectors,
} from '../../../slices/DataSourceControllerSlice/selectors';
import { getInterfaceRecordSelectors } from '../../InterfaceStore/runner/getInterfaceRecordSelectors';
import type { GlobalAppStateStoreType } from './types';

export const createGlobalAppStateStoreSelectors = (
  store: UseBoundStore<StoreApi<GlobalAppStateStoreType>>,
) => {
  const { dataSourceKeysSelectors } = createDataSourceControllerSliceKeysSelectors(store);
  const storeSelectors = {
    useLocation: () => store((state) => state.location),
    useUserContext: () => store((state) => state.userContext),
    useDeviceContext: () => store((state) => state.deviceContext),
    useAppsAndIntegrations: () => store((state) => state.appsAndIntegrations),
    useDeviceDetails: () => store((state) => state.deviceDetails),
    useNavigation: () => store((state) => state.navigation),
    useIsNavigationDrawerOpen: () => store((state) => state.navigation.isDrawerOpen),
    useIsNotificationPanelOpen: () => store((state) => state.navigation.isNotificationPanelOpen),
    useUnopenedNotificationsCount: () =>
      store((state) => state.navigation.unopenedNotificationsCount),
    usePermissions: () => store((state) => state.permissions),
    useEnvVariables: () => store((state) => state.envVariables),
    useClient: () => store((state) => state.client),
    useMode: () => store((state) => state.mode),
    useActions: () => store.getState().actions,
    useBasePath: () => store((state) => state.basePath),
    useCommonNamespace: () => store((state) => state.commonNamespace),
    useLocale: () => store((state) => state.commonNamespace?.properties.locale),
    useActiveAppVariantKey: () => store((state) => state.activeAppVariantKey),
    useLocalStorage: () => store((state) => state.localStorage),
    useSessionStorage: () => store((state) => state.sessionStorage),
    usePageVariables: () => store((state) => state.pageVariables),
    usePageFunctions: () => store((state) => state.pageFunctions),
    useColorSchemeDetails: () => store((state) => state.colorSchemeDetails),
    useInterfaceThemeId: () => store((state) => state.interfaceThemeId),
    useActiveTheme: () => store((state) => state.activeTheme),
    useAvailableThemes: () => store((state) => state.availableThemes),
    ...dataSourceKeysSelectors,
  };

  const { dataSourceSelectors } = createDataSourceControllerSliceSelectors(store);

  const recordSelectors = getInterfaceRecordSelectors(store);

  const pageFunctionSelectors = {
    useResult: (pageFunctionId: string) => {
      return store((state) => state.pageFunctions[pageFunctionId]);
    },
  };

  const pageVariableSelectors = {
    useResult: (pageVariableId: string) => {
      return store((state) => state.pageVariables[pageVariableId]);
    },
  };

  return Object.assign(store, {
    hooks: {
      ...storeSelectors,
      record: recordSelectors,
      dataSource: dataSourceSelectors,
      pageFunction: pageFunctionSelectors,
      pageVariable: pageVariableSelectors,
    },
  });
};

export type GlobalAppStateStoreInstance = ReturnType<typeof createGlobalAppStateStoreSelectors>;
